import { View, ViewProps } from 'react-native';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from '@/utils/cn';

const cardVariants = cva(
  'bg-surface-secondary border border-border rounded-lg overflow-hidden',
  {
    variants: {
      variant: {
        default: 'bg-surface-secondary border-border',
        elevated: 'bg-surface-elevated border-border shadow-md',
        outlined: 'bg-transparent border-border border-2',
        filled: 'bg-surface-tertiary border-transparent',
      },
      size: {
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
      },
      hoverable: {
        true: 'hover:bg-surface-tertiary hover:border-border-primary/50 transition-colors cursor-pointer',
        false: '',
      },
      interactive: {
        true: 'active:scale-[0.98] transition-transform',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      hoverable: false,
      interactive: false,
    },
  }
);

export interface CardProps
  extends ViewProps, VariantProps<typeof cardVariants> {
  className?: string;
  children: React.ReactNode;
}

const Card = forwardRef<View, CardProps>(
  ({ className, variant, size, hoverable, interactive, ...props }, ref) => {
    return (
      <View
        ref={ref}
        className={cn(
          cardVariants({ variant, size, hoverable, interactive }),
          className
        )}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

// Card sub-components
const CardHeader = forwardRef<
  View,
  ViewProps & { className?: string; children: React.ReactNode }
>(({ className, ...props }, ref) => (
  <View
    ref={ref}
    className={cn('flex flex-col space-y-1.5 pb-4', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = forwardRef<
  View,
  ViewProps & { className?: string; children: React.ReactNode }
>(({ className, ...props }, ref) => (
  <View
    ref={ref}
    className={cn('text-2xl font-semibold text-text-heading', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = forwardRef<
  View,
  ViewProps & { className?: string; children: React.ReactNode }
>(({ className, ...props }, ref) => (
  <View
    ref={ref}
    className={cn('text-sm text-text-secondary', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = forwardRef<
  View,
  ViewProps & { className?: string; children: React.ReactNode }
>(({ className, ...props }, ref) => (
  <View ref={ref} className={cn('pb-4', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = forwardRef<
  View,
  ViewProps & {
    className?: string;
    children: React.ReactNode;
    justify?: 'start' | 'end' | 'center' | 'between';
  }
>(({ className, justify = 'end', ...props }, ref) => (
  <View
    ref={ref}
    className={cn(
      'flex flex-row items-center pt-4',
      justify === 'start' && 'justify-start',
      justify === 'end' && 'justify-end',
      justify === 'center' && 'justify-center',
      justify === 'between' && 'justify-between',
      className
    )}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  cardVariants,
};
